'use client';
import { FC, useState, useEffect } from "react";
import { SliceComponentProps, PrismicRichText } from "@prismicio/react";
import NextLink from "next/link";
import LinkToWeb from "../../assets/images/icons/link-to-web.svg";
import _ from "lodash";
import { Content } from "@prismicio/client";

export type RspInformerListProps =
  SliceComponentProps<Content.RspInformerListSlice>;

type Informer = {
  id: string;
  title: string;
  link: string;
  last_update: string;
};

const RspInformerList: FC<RspInformerListProps> = ({ slice }) => {
  const toDate = (value: string) => new Date(value);
  const formatYear = (value: string) => String(toDate(value).getFullYear());
  const formatMonth = (value: string) => String(toDate(value).getMonth() + 1).padStart(2, "0");
  const formatDDMMYY = (value: string) => {
    const date = toDate(value);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  };
  const formatMonthLabel = (month: string) => {
    const monthIndex = Number(month) - 1;
    return new Intl.DateTimeFormat("en-NZ", { month: "long" }).format(new Date(2000, monthIndex, 1));
  };

  const [informers, setInformers] = useState<Informer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchInformers() {
      try {
        const res = await fetch("/api/get-remote-data", {
          cache: "force-cache", // will honor the 24h cache headers from the API
        });
        const data = await res.json();
        setInformers(data.campaignData || []);
      } catch (err) {
        console.error("Failed to fetch informers:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchInformers();
  }, []);

  // group + order just like your existing logic
  const displayData: any[] = [];
  let displayYear: string[] = [];
  let displayMonth: string[] = [];
  let displayLinks: Informer[] = [];

  informers?.forEach((item) => {
    if (item.last_update !== "") {
      displayYear.push(formatYear(item.last_update));
      displayMonth.push(formatMonth(item.last_update));
      displayLinks.push(item);
    }
  });

  displayLinks = _.orderBy(displayLinks, ["last_update"], ["desc"]);

  displayYear = _.reverse(_.sortBy(_.uniq(displayYear)));
  displayMonth = _.reverse(_.sortBy(_.uniq(displayMonth)));

  displayYear.forEach((year, yindex) => {
    displayData.push({ year, months: [] as any[] });
    displayMonth.forEach((month) => {
      const monthObj = { month, links: [] as Informer[] };
      displayLinks.forEach((link) => {
        const linkYear = formatYear(link.last_update);
        const linkMonth = formatMonth(link.last_update);
        if (year === linkYear && month === linkMonth) {
          monthObj.links.push(link);
        }
      });
      displayData[yindex].months.push(monthObj);
    });
  });

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="font-fellix"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mt-24">
          <PrismicRichText field={slice.primary.headline} />
          <div className="text-[24px] mt-16">
            <PrismicRichText field={slice.primary.sub_text} />
          </div>
        </div>

        <br />
        <br />

        {/* keep your teaser cards from slice.primary.list */}
        {/* <div className="grid grid-cols-12 gap-6">
          {slice.primary.list.map((item: any, index: number) => {
            if (!showAll && index > 2) return null;
            return (
              <div
                className="col-span-12 md:col-span-4 relative h-[540px] mb-16"
                key={index}
              >
                <img src={item.image.url} alt={item.image.alt} />
                <div className="grid grid-cols-12 my-4">
                  <div className="col-span-7 font-semibold text-[20px] leading-[24px]">
                    <PrismicRichText field={item.title} />
                  </div>
                  <div className="col-span-5 flex justify-end">
                    {Moment(item.date).format("DD/MM/YY")}
                  </div>
                </div>
                <PrismicRichText field={item.description} />
                <a
                  className="text-blue font-medium absolute bottom-0"
                  href={"/articles/" + item.link.uid}
                >
                  Read more &gt;
                </a>
              </div>
            );
          })}
        </div>

        <div
          className="w-full max-w-[1440px] mx-auto my-12 text-center"
          ref={buttonRef}
        >
          <div
            className="border-jade border-[1px] text-jade hover:text-white hover:bg-jade rounded-full h-[56px] inline-flex px-8 items-center justify-center duration-300 cursor-pointer"
            onClick={() => setShowAll(!showAll)}
          >
            See more
          </div>
        </div> */}

        {/* grouped archive */}
        <div className="mt-8 w-full">
          {loading && <p className="text-center my-24">Loading …</p>}
          {!loading &&
            displayData.map((year, yindex) => (
              <div
                className="grid grid-cols-12 border-t-[1px] border-t-jade g-0 w-full"
                style={{ paddingBottom: 100 }}
                key={yindex}
              >
                <div className="col-span-12 mb-16">
                  <h2 className="pt-[40px]">{year.year}</h2>
                </div>
                {year.months.map((month: any, mindex: number) => {
                  if (month.links.length > 0) {
                    return (
                      <div className="col-span-12 mb-12" key={mindex}>
                        <h2 className="mb-4">
                          {formatMonthLabel(month.month)}
                        </h2>
                        {month.links.map((link: any, index: number) => (
                          <NextLink href={link.link} key={index}>
                            <div className="grid grid-cols-12 border-t-[1px] border-t-jade g-0">
                              <div className="col-span-12 md:col-span-2 py-4">
                                <p>
                                  {formatDDMMYY(link.last_update)}
                                </p>
                              </div>
                              <div className="col-span-12 md:col-span-9 py-4">
                                <p>{link.title}</p>
                              </div>
                              <div className="col-span-12 md:col-span-1 flex justify-end py-4">
                                <LinkToWeb className="relative top-[5px]" />
                              </div>
                            </div>
                          </NextLink>
                        ))}
                      </div>
                    );
                  }
                })}
              </div>
            ))}
        </div>
      </div>
    </section>
  );
};

export default RspInformerList;
